import {openDialog, showNotification} from 'platform/components';
import {useLocale} from 'platform/locale';

import {isNotNil} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetParticipationQuery} from '@dms/api/core';
import {useGetUserQuery} from '@dms/api/customer';
import {
  useGetBusinessCaseQuery,
  useGetVehicleQuery,
  useLazyGetBusinessCaseQuery,
  useSendBusinessCaseBuyingOnlineOfferMutation,
} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {InvalidateOnlineOfferDialog} from '../components/InvalidateOnlineOfferDialog/InvalidateOnlineOfferDialog';
import {SendOnlineOfferEmailForm} from '../components/SendOnlineOfferEmailForm/SendOnlineOfferEmailForm';
import {usePermissions} from '../hooks/usePermissions/usePermissions';
import {getBuyingOnlineOfferActionPermissions} from '../utils/getBuyingOnlineOfferActionPermissions';
import {getBuyingOnlineOfferUri} from '../utils/getBuyingOnlineOfferUri';
import {handleApiError} from '../utils/handleApiError';

export const useBcToBuyingOnlineOffer = (businessCaseId: string) => {
  const {language} = useLocale();
  const {data: businessCaseData} = useGetBusinessCaseQuery({businessCaseId});
  const [getBusinessCase, {isLoading: isGetBusinessCaseLazyQueryLoading}] =
    useLazyGetBusinessCaseQuery();
  const vehicleId = businessCaseData?.offers[0]?.purchaseVehicles?.[0]?.vehicleId;
  const {data: vehicleData} = useGetVehicleQuery(
    {vehicleId: vehicleId ?? ''},
    {skip: isNilOrEmpty(vehicleId)}
  );
  const {data: participation} = useGetParticipationQuery({
    recordId: businessCaseId,
    resourceId: EntityResourceIds.businessCase,
  });
  const {data: ownerInfo} = useGetUserQuery(
    {id: participation?.owner?.userId ?? ''},
    {skip: isNilOrEmpty(participation?.owner?.userId)}
  );
  const [createBuyingOnlineOffer, {isLoading: isCreateBuyingOnlineOfferMutationLoading}] =
    useSendBusinessCaseBuyingOnlineOfferMutation();
  const [canSendBuyingOnlineOffer, canInvalidateBuyingOnlineOffer] = usePermissions({
    permissionKeys: ['sendBuyingOnlineOffer', 'invalidateBuyingOnlineOffer'],
    scopes: {
      sendBuyingOnlineOffer: {
        participation,
        branchId: businessCaseData?.branchId,
      },
      invalidateBuyingOnlineOffer: {
        participation,
        branchId: businessCaseData?.branchId,
      },
    },
  });
  const isOnlineOfferCreated = isNotNilOrEmpty(businessCaseData?.offerId);
  const isOnlineOfferSent = isNotNilOrEmpty(businessCaseData?.onlineOfferFirstSentAt);
  const actionPermissions = getBuyingOnlineOfferActionPermissions({
    businessCaseInternalType: businessCaseData?.businessCaseInternalType,
    businessCaseState: businessCaseData?.businessCaseState,
    isOnlineOfferCreated,
    isOnlineOfferSent,
    canSendBuyingOnlineOffer,
    canInvalidateBuyingOnlineOffer,
  });

  const openSendOnlineOfferDialog = ({
    offerId,
    onSuccess,
  }: {
    offerId: string;
    onSuccess?: () => void;
  }) => {
    if (!(businessCaseData && participation?.owner?.userId && vehicleData)) {
      return;
    }

    const coverPhotoOfVehicle = vehicleData.photos?.find((photo) => photo.isCoverPhoto);

    openDialog(
      <SendOnlineOfferEmailForm
        onlineOfferKind="BUYING"
        businessCaseData={businessCaseData}
        offerId={offerId}
        sellerId={participation.owner.userId}
        vehicleId={vehicleData.id}
        photoId={coverPhotoOfVehicle?.fileId}
        onSuccess={onSuccess}
      />,
      {
        title: i18n.t('entity.sendEmailOnlineOfferDialog.heading'),
        id: 'sendBuyingOnlineOfferDialog',
        'data-testid': 'sendBuyingOnlineOfferDialog',
        scrollBehavior: 'inside',
        size: 'large',
        disableMaxHeight: true,
      }
    );
  };

  const ensureOfferId = () =>
    createBuyingOnlineOffer({businessCaseId})
      .unwrap()
      .then(() => getBusinessCase({businessCaseId}).unwrap())
      .then((newBusinessCaseData) => {
        if (isNotNil(newBusinessCaseData.offerId)) {
          return newBusinessCaseData.offerId;
        }

        showNotification.error();
        return null;
      })
      .catch(handleApiError)
      .then((offerId) => offerId ?? null);

  const sendOnlineOfferToCustomer = ({
    shouldCreateOffer = false,
    onSuccess,
  }: {
    shouldCreateOffer?: boolean;
    onSuccess?: () => void;
  } = {}) => {
    if (shouldCreateOffer) {
      return ensureOfferId().then((offerId) => {
        if (isNotNil(offerId)) {
          openSendOnlineOfferDialog({offerId, onSuccess});
        }
      });
    }

    const offerId = businessCaseData?.offerId;

    if (isNotNil(offerId)) {
      openSendOnlineOfferDialog({offerId, onSuccess});
    }
  };

  const invalidateOnlineOfferToCustomer = () => {
    if (businessCaseData?.offerId) {
      openDialog(
        <InvalidateOnlineOfferDialog offerId={businessCaseData.offerId} onlineOfferKind="BUYING" />,
        {
          title: i18n.t('entity.invalidateOnlineOfferDialog.title'),
          id: 'invalidateOnlineOfferDialog',
          'data-testid': 'invalidateOnlineOfferDialog',
        }
      );
    }
  };

  const openOffer = () => {
    if (isOnlineOfferCreated) {
      const offerId = businessCaseData?.offerId;

      if (isNotNil(offerId)) {
        window
          .open(
            getBuyingOnlineOfferUri({offerId, language, sellerEmail: ownerInfo?.email}),
            '_blank'
          )
          ?.focus();
      }

      return;
    }

    return ensureOfferId().then((offerId) => {
      if (isNotNil(offerId)) {
        window
          .open(
            getBuyingOnlineOfferUri({offerId, language, sellerEmail: ownerInfo?.email}),
            '_blank'
          )
          ?.focus();
      }
    });
  };

  return {
    ...actionPermissions,
    isOnlineOfferCreated: actionPermissions.isBuyingOnlineOfferEnabled && isOnlineOfferCreated,
    isOnlineOfferSent: actionPermissions.isBuyingOnlineOfferEnabled && isOnlineOfferSent,
    isOpenOnlineOfferLoading:
      isGetBusinessCaseLazyQueryLoading || isCreateBuyingOnlineOfferMutationLoading,
    actions: {
      sendOnlineOfferToCustomer,
      invalidateOnlineOfferToCustomer,
      openOffer,
    },
  };
};
